import { useState, useEffect } from "react";
import { getAutographImagesByUserId, getKiinteistoSasToken } from '../../../api/api';
import { useUser } from "../../../context/UserContext";
import { Form, Button } from "react-bootstrap";
import config from "../../../devprodConfig";

export default function SignatureBlock({ data, onChange }) {
  const { localUser, loading } = useUser();
  const userId = localUser?.id;

  const [autograph, setAutograph] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [savedImages, setSavedImages] = useState([]);
  const [currentDate, setCurrentDate] = useState("");


  useEffect(() => setCurrentDate(new Date().toISOString()), []);

  useEffect(() => {
    if (!userId) return;
    fetchAutograph();
  }, [userId]);

  useEffect(() => {
    if (!autograph) return;
    let abort = false;
    let objectUrl = null;

    const fetchBlob = async () => {
      try {
        const blobName = autograph.azure_blob_name;
        if (!blobName) return;

        const res = await getKiinteistoSasToken(blobName);
        const sasUrl = res?.data?.sasUrl;
        if (!sasUrl) throw new Error("No SAS URL returned");

        const response = await fetch(sasUrl);
        if (!response.ok) throw new Error(`Failed to fetch blob: ${response.status}`);

        const blob = await response.blob();
        if (abort) return;

        objectUrl = URL.createObjectURL(blob);
        setImageUrl(objectUrl);
      } catch (err) {
        console.error("Failed to fetch autograph blob via SAS:", err);
        setImageUrl(null);
      }
    };

    fetchBlob();

    return () => {
      abort = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [autograph]);

  useEffect(() => {
    const signatures = Array.isArray(data?.value) ? data.value : data?.value ? [data.value] : [];
    let abort = false;
    const objectUrls = [];

    const fetchAllSavedBlobs = async () => {
      try {
        const results = await Promise.all(signatures.map(async sig => {
          const res = await getKiinteistoSasToken(sig.azure_blob_name);
          const sasUrl = res?.data?.sasUrl;
          if (!sasUrl) return null;

          const response = await fetch(sasUrl);
          if (!response.ok) return null;

          const blob = await response.blob();
          if (abort) return null;

          const objectUrl = URL.createObjectURL(blob);
          objectUrls.push(objectUrl);
          return { signature: sig, objectUrl };
        }));

        setSavedImages(results.filter(r => r));
      } catch (err) {
        console.error("Failed to fetch saved signatures:", err);
        setSavedImages([]);
      }
    };

    fetchAllSavedBlobs();

    return () => {
      abort = true;
      objectUrls.forEach(url => URL.revokeObjectURL(url));
    };
  }, [data?.value]);

  const fetchAutograph = async () => {
    try {
      const res = await getAutographImagesByUserId(userId);
      setAutograph(res?.data?.[0] || null);
    } catch (err) {
      console.error("Failed to fetch autograph:", err);
      setAutograph(null);
    }
  };

  const handleSignReport = () => {
    if (!localUser || !autograph) return;

    const newSignature = {
      signatureId: autograph.id,
      azure_blob_name: autograph.azure_blob_name,
      date: currentDate,
      user: {
        nimi: localUser.nimi || "",
        sahkoposti: localUser.sahkoposti || "",
        puhelinnumero: localUser.puhelinnumero || "",
        ammattinimike: localUser.ammattinimike || ""
      }
    };

    const updated = Array.isArray(data.value) ? [...data.value, newSignature] : [newSignature];
    onChange({ ...data, value: updated });
  };

  const handleRemoveSignature = (signatureId) => {
    const updated = data.value.filter(sig => sig.signatureId !== signatureId);
    onChange({ ...data, value: updated });
  };

  if (loading) return <div>Ladataan allekirjoituksia...</div>;

  const missingInfo = !localUser?.nimi || !localUser?.sahkoposti || !localUser?.puhelinnumero || !localUser?.ammattinimike;

  return (
    <div className="mb-3">
      {/* Always visible profile info button */}


        {/* Sign report button */}
        {autograph && (
            <div className="text-center mb-2">
            <Button onClick={handleSignReport} disabled={!autograph || missingInfo} variant="success">
                Allekirjoita raportti
            </Button>
            </div>
        )}

        <div className="text-center mb-3">
        <Button
            style={{ padding: "0.25rem 0.75rem", fontSize: "0.60rem" }}
            variant={missingInfo ? "warning" : "secondary"}
            onClick={() => window.open(`${window.location.origin}/Profile`, "_blank")}
        >
            {missingInfo ? "Täydennä profiilitietosi" : "Päivitä profiilitietosi"}
        </Button>
        </div>


      <div className="text-center">
        {savedImages.map(({ signature, objectUrl }) => (
          <div key={signature.signatureId || signature.azure_blob_name} className="mb-3">
            <img
              src={objectUrl}
              alt="Allekirjoitus"
              style={{ maxHeight: 120, objectFit: "contain", borderRadius: 4 }}
            />
            <div>
              <strong>Päivämäärä:</strong> {new Date(signature.date).toLocaleDateString("fi-FI")}
            </div>
            <Form className="mt-2 text-center">
              <Form.Group className="mb-2">
                <Form.Label>Nimi</Form.Label>
                <Form.Control type="text" value={signature.user.nimi} className="text-center" disabled />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Sähköposti</Form.Label>
                <Form.Control type="email" value={signature.user.sahkoposti} className="text-center" disabled />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Puhelinnumero</Form.Label>
                <Form.Control type="text" value={signature.user.puhelinnumero} className="text-center" disabled />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Ammattinimike</Form.Label>
                <Form.Control type="text" value={signature.user.ammattinimike} className="text-center" disabled />
              </Form.Group>
              <Button
                variant="danger"
                onClick={() => handleRemoveSignature(signature.signatureId)}
              >
                Poista allekirjoitus
              </Button>
            </Form>
          </div>
        ))}

        {!imageUrl && savedImages.length === 0 && (
          <div className="mt-2 text-muted">Allekirjoituksia ei vielä tallennettu.</div>
        )}
      </div>
    </div>
  );
}
